import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { SunscreenSpotlight } from "@/components/SunscreenSpotlight";
import { CATEGORY_LABELS, usd } from "@/lib/format";
import { DEPT_BY_SLUG } from "@/lib/departments";
import { CATEGORY_BANNERS } from "@/lib/categoryBanners";
import { AgeGate, ComplianceNotice, AGE_RESTRICTED_CATEGORIES, isAgeVerified } from "@/components/AgeGate";
import { WholesaleQuote } from "@/components/WholesaleQuote";
import { SlidersHorizontal, Loader2, ShieldCheck, Truck, BadgeCheck, Container } from "lucide-react";
import { Pagination } from "@/components/Pagination";

const SORTS = [
  { v: "", l: "Featured" }, { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" }, { v: "rating", l: "Top Rated" }, { v: "discount", l: "Biggest Discount" },
];

const FINISH_COLORS = {
  "Satin Nickel": "#B8BEC4",
  "Polished Brass": "#C9A227",
  "Antique Brass": "#8A6E3B",
  "Matte Black": "#1F1F1F",
  "Polished Chrome": "#D6DBDF",
};

const PAGE = 20;

export default function Listing() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const mc = sp.get("mc") || "";
  const dept = sp.get("dept") || "";
  const sub = sp.get("sub") || "";
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [subcats, setSubcats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [parish, setParish] = useState("");
  const [parishes, setParishes] = useState([]);
  const [maxPrice, setMaxPrice] = useState("");
  const [finish, setFinish] = useState("");
  const restricted = AGE_RESTRICTED_CATEGORIES.includes(slug);
  const [ageOk, setAgeOk] = useState(isAgeVerified());

  useEffect(() => { api.get("/meta").then(({ data }) => setParishes(data.parishes)).catch(() => {}); }, []);
  const [catCount, setCatCount] = useState(null);

  useEffect(() => {
    if (!slug) { setSubcats([]); return; }
    api.get("/categories").then(({ data }) => {
      const cat = data.find((c) => c.slug === slug);
      setSubcats((cat && cat.subcategories) || []);
      setCatCount(cat ? cat.count : null);
    }).catch(() => setSubcats([]));
  }, [slug]);

  const buildParams = (pg) => {
    const params = { limit: PAGE, skip: pg * PAGE };
    if (slug) params.category = slug;
    if (mc) params.main_category = mc;
    if (dept) params.dept = dept;
    if (sub) params.subcategory = sub;
    if (q) params.q = q;
    if (sort) params.sort = sort;
    if (parish) params.parish = parish;
    if (maxPrice) params.max_price = Number(maxPrice);
    return params;
  };

  useEffect(() => {
    if (restricted && !ageOk) { setLoading(false); return; }
    setLoading(true); setPage(0);
    api.get("/products", { params: buildParams(0) }).then(({ data, headers }) => {
      setItems(data); setHasMore(data.length === PAGE); setTotal(Number(headers["x-total-count"]) || data.length); setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, q, mc, dept, sub, sort, parish, maxPrice, restricted, ageOk]);

  const loadMore = () => {
    const next = page + 1; setLoadingMore(true);
    api.get("/products", { params: buildParams(next) }).then(({ data }) => {
      setItems((prev) => [...prev, ...data]); setPage(next);
      setHasMore(data.length === PAGE); setLoadingMore(false);
    }).catch(() => setLoadingMore(false));
  };
  const goPage = (p1) => {
    const pg = p1 - 1; setLoadingMore(true);
    api.get("/products", { params: buildParams(pg) }).then(({ data }) => { setItems(data); setPage(pg); setHasMore(data.length === PAGE); setLoadingMore(false); }).catch(() => setLoadingMore(false));
  };
  const pages = Math.max(1, Math.ceil((total || 0) / PAGE));

  useEffect(() => { setFinish(""); }, [slug, sub]);

  const subName = subcats.find((s) => s.slug === sub)?.name;
  const title = mc || DEPT_BY_SLUG[dept]?.name || (q ? `Results for "${q}"` : (CATEGORY_LABELS[slug] || "All Products"));
  const finishOptions = [...new Set(items.map((p) => p.finish).filter(Boolean))].sort();
  const shown = finish ? items.filter((p) => p.finish === finish) : items;
  const bannerImg = slug ? CATEGORY_BANNERS[slug] : null;

  if (restricted && !ageOk) return <AgeGate onVerified={() => setAgeOk(true)} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Catalogue-style category banner */}
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden mb-6" data-testid="catalog-banner">
        {bannerImg && (
          <>
            <img src={bannerImg} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
          </>
        )}
        <div className="relative px-6 sm:px-10 py-9 sm:py-12">
          <nav className="text-xs sm:text-sm text-white/50 mb-4"><Link to="/" className="hover:text-emerald">Home</Link> <span className="mx-1">/</span> <span className="text-white/90 font-medium">{title}</span></nav>
          <div className="flex items-center gap-4 mb-4"><div className="h-1.5 w-16 bg-emerald rounded-full" /><Link to="/snap" className="inline-flex items-center gap-1 rounded-full border border-white/30 text-white px-3 py-1 text-[11px] font-semibold hover:bg-white/10" data-testid="listing-snap-link">📷 Snap a photo to find it</Link></div>
          <h1 className="font-clash uppercase text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95]">{title}</h1>
          <p className="text-white/60 mt-3 text-sm" data-testid="listing-count">{(!q && !sub && catCount != null) ? `${catCount} product${catCount === 1 ? "" : "s"}` : `${shown.length}${hasMore && !finish ? "+" : ""} product${shown.length === 1 ? "" : "s"}`}{subName ? ` · ${subName}` : ""} · from Kingston to the world 🌍</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-white/70 text-[11px] font-bold uppercase tracking-[0.15em]">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald" /> Secure Checkout</span>
            <span className="flex items-center gap-1.5"><Truck size={13} className="text-emerald" /> Fast Delivery</span>
            <span className="flex items-center gap-1.5"><BadgeCheck size={13} className="text-emerald" /> Verified Sellers</span>
          </div>
        </div>
      </div>

      {subcats.length > 0 && !q && !mc && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="subcategory-chips">
          <Link to={`/category/${slug}`} data-testid="subcat-all"
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${!sub ? "bg-ink text-white border-ink" : "bg-white text-ink border-border hover:border-ink"}`}>
            All
          </Link>
          {subcats.map((s) => (
            <Link key={s.slug} to={`/category/${slug}?sub=${s.slug}`} data-testid={`subcat-${s.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${sub === s.slug ? "bg-emerald text-white border-emerald" : "bg-white text-ink border-border hover:border-emerald"}`}>
              {s.name}
            </Link>
          ))}
        </div>
      )}

      {restricted && (
        <ComplianceNotice text="These are tobacco products intended for adults 18 years or older. Tobacco use is harmful to health. Sale to minors is prohibited by law." />
      )}

      {slug === "beauty" && !q && !mc && <SunscreenSpotlight />}

      {slug === "water" && !q && !mc && (
        <div className="mb-8 rounded-2xl border border-emerald/30 bg-emerald/5 overflow-hidden" data-testid="water-wholesale">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-emerald/20">
            <div className="flex items-center gap-2">
              <Container size={18} className="text-emerald" />
              <h2 className="font-display font-bold text-lg">Buying by the container? Get a wholesale quote</h2>
            </div>
            <Link to="/quote" className="text-sm font-semibold text-emerald hover:underline shrink-0" data-testid="water-quote-link">Open full page →</Link>
          </div>
          <div className="p-5">
            <WholesaleQuote />
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink"><SlidersHorizontal size={16} className="text-emerald" /> Filter &amp; Sort</div>
        <div className="flex flex-wrap gap-2">
          {sub && finishOptions.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="filter-finish">
              <button type="button" onClick={() => setFinish("")} data-testid="finish-all"
                className={`px-3 py-2 rounded-xl text-sm border transition-colors ${!finish ? "border-ink bg-ink text-white" : "border-border bg-white hover:border-ink"}`}>
                All Finishes
              </button>
              {finishOptions.map((f) => (
                <button type="button" key={f} onClick={() => setFinish(f)} data-testid={`finish-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border transition-colors ${finish === f ? "border-emerald bg-emerald/10 text-ink" : "border-border bg-white hover:border-emerald"}`}>
                  <span className="h-3.5 w-3.5 rounded-full border border-black/10 shrink-0" style={{ background: FINISH_COLORS[f] || "#999" }} />
                  {f}
                </button>
              ))}
            </div>
          )}
          <select value={parish} onChange={(e) => setParish(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="filter-parish">
            <option value="">All Parishes</option>
            {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="filter-price">
            <option value="">Any Price</option>
            <option value="5000">Under {usd(5000)}</option>
            <option value="20000">Under {usd(20000)}</option>
            <option value="50000">Under {usd(50000)}</option>
            <option value="150000">Under {usd(150000)}</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="filter-sort">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald" size={32} /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">
          <SlidersHorizontal className="mx-auto mb-3 text-slate-300" size={40} />
          No products match your filters. Try broadening your search.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {shown.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          {!finish && total > PAGE && <Pagination page={page + 1} pages={pages} total={total} from={page * PAGE + 1} to={Math.min((page + 1) * PAGE, total)} onPage={goPage} />}
          {hasMore && !finish && total <= PAGE && (
            <div className="flex justify-center mt-10">
              <button onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn"
                className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-8 py-3.5 hover:bg-emerald transition-colors disabled:opacity-60">
                {loadingMore && <Loader2 className="animate-spin" size={18} />}
                {loadingMore ? "Loading…" : "Load More"}
              </button>
            </div>
          )}
        </>
      )}

      <div className="mt-4 -mx-4 sm:-mx-6 lg:-mx-8">
        <RecentlyViewed />
      </div>
    </div>
  );
}
